import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination
} from 'src/registry/components/table';

import usePagination from 'src/registry/hooks/usePagination';
import { Entity, ENTITY_TYPE } from 'src/features/entity/types';
import { useListEntities } from 'src/features/entity/api/useListEntities';
import { entityMessages } from 'src/features/entity/utils/messages';
import CellEntityActions from 'src/features/entity/components/cell/CellEntityActions';
import useSorting from 'src/registry/hooks/useSorting';
import useSearch from 'src/registry/hooks/useSearch';
import CreateEntityPersonFormModal from 'src/features/entity/components/forms/CreateEntityPersonFormModal';
import { PATH } from 'src/features/navigation/utils/constants';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

type PeopleTableProps = {
    parentId?: string;
}

const columnHelper = createColumnHelper<Entity>();

const PeopleTable = ({ parentId }: PeopleTableProps) => {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { parkingId } = useParking();
    const manageEntityPermission = useHasPermissions([PERMISSION.MANAGE_ENTITIES]);

    const {
        data: listEntities,
        isError,
        isLoading,
    } = useListEntities({
        parkingId,
        type: ENTITY_TYPE.PERSON,
        ...(parentId && { parent: parentId }),
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
    });

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <div className='flex flex-col'>
                        <CellLink to={`/${PATH.ENTITIES}/${ENTITY_TYPE.PERSON}/${ctx.row.original.id}`} {...ctx} />
                        <div className='text-gray-600'>{ctx.row.original.identifier}</div>
                    </div>
                )
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.name),
            },
        }),
        columnHelper.accessor('phone', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.phone),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.email),
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.description),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (ctx) => (
                <CellEntityActions {...ctx} type={ENTITY_TYPE.PERSON}/>
            ),
            maxSize: 45
        }),
    ];

    const placeholder = `${formatMessage(entityMessages.name)}, ${formatMessage(entityMessages.phone)}, ${formatMessage(entityMessages.email)}`;

    return (
            <DataTable
                columns={columns as any}
                data={listEntities}
                pagination={pagination}
                sorting={sorting}
                search={search}
                semantics={{
                    title: formatMessage(entityMessages.people),
                    entryName: formatMessage(entityMessages.entity).toLowerCase(),
                    entryNames: formatMessage(entityMessages.entities).toLowerCase(),
                    placeholder: placeholder
                }}
                isError={isError}
                isLoading={isLoading}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: !parentId && manageEntityPermission ? CreateEntityPersonFormModal : undefined,
                }}
            />
    );
};

export default PeopleTable;
